import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { fold } from '../lib/fold';

/** How many matches the list shows; typing more narrows it. */
const SHOWN_MAX = 50;

/**
 * A text field with its own suggestion list — the replacement for
 * `<datalist>`, which mobile browsers render badly or not at all. The text is
 * the value: typing something not in `options` is allowed, and the list then
 * ends with "Dodaj nowy" to say so. Picking an option calls `onPick`, which
 * usually fills more of the form than just the name.
 *
 * The list opens on typing, not on focus, so tapping into the field does not
 * cover the form with every option; an empty field shows the list only when
 * the arrow keys ask for it.
 *
 * Closes on a pointer down outside it rather than on blur: a tap on an option
 * blurs the input first on touch screens, and must still land.
 */
export function Combobox<T>({
  id,
  label,
  value,
  onChange,
  options,
  optionLabel,
  renderOption,
  optionKey,
  onPick,
  newLabel,
  required,
  maxLength,
  autoFocus,
  onKeyDown: onOtherKeyDown,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly T[];
  /** What is matched against and written into the field when picked. */
  optionLabel: (option: T) => string;
  /** The row in the list; the label alone when omitted. */
  renderOption?: (option: T) => ReactNode;
  optionKey: (option: T) => string;
  onPick: (option: T) => void;
  /** The trailing row for text that matches no option exactly. */
  newLabel?: (text: string) => string;
  required?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
  /** Keys the list does not use itself, such as Enter with no option active. */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  // Set by the arrow keys: the one way to see every option in an empty field.
  const [browsing, setBrowsing] = useState(false);
  const [active, setActive] = useState(-1);

  const query = fold(value);
  const matches = useMemo(
    () =>
      (query === ''
        ? options
        : options.filter((o) => fold(optionLabel(o)).includes(query))
      ).slice(0, SHOWN_MAX),
    [options, optionLabel, query],
  );
  const exact = options.some((o) => fold(optionLabel(o)) === query);
  const showNew = newLabel !== undefined && query !== '' && !exact;
  const rows = matches.length + (showNew ? 1 : 0);
  // Checked here rather than on change, so a value the parent empties (the
  // tag input after adding a tag) closes the list too.
  const expanded = open && rows > 0 && (query !== '' || browsing);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (active < 0) return;
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function pick(index: number) {
    const option = matches[index];
    if (option !== undefined) {
      onChange(optionLabel(option));
      onPick(option);
    }
    // The "Dodaj nowy" row keeps the text as typed.
    setOpen(false);
    setBrowsing(false);
    setActive(-1);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setBrowsing(true);
      setActive((i) => (rows === 0 ? -1 : (i + 1) % rows));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setBrowsing(true);
      setActive((i) => (rows === 0 ? -1 : (i - 1 + rows) % rows));
    } else if (e.key === 'Enter' && expanded && active >= 0) {
      // Picks instead of submitting the form.
      e.preventDefault();
      pick(active);
    } else if (e.key === 'Escape' && expanded) {
      e.preventDefault();
      setOpen(false);
      setBrowsing(false);
      setActive(-1);
    } else if (e.key === 'Tab') {
      setOpen(false);
      setBrowsing(false);
    } else {
      onOtherKeyDown?.(e);
    }
  }

  const optionId = (index: number) => `${listId}-${index}`;

  return (
    <div ref={rootRef} className="combobox">
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-activedescendant={
          expanded && active >= 0 ? optionId(active) : undefined
        }
        autoComplete="off"
        autoFocus={autoFocus}
        required={required}
        maxLength={maxLength}
        className="field-input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setBrowsing(false);
          setActive(-1);
        }}
        onKeyDown={onKeyDown}
      />
      {expanded && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={label}
          className="popover combobox-list"
        >
          {matches.map((option, index) => (
            <li
              key={optionKey(option)}
              id={optionId(index)}
              data-index={index}
              role="option"
              aria-selected={index === active}
              className={
                index === active
                  ? 'combobox-option combobox-option-active'
                  : 'combobox-option'
              }
              // Keeps focus in the input, so the keyboard stays up on a phone.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(index)}
            >
              {renderOption ? renderOption(option) : optionLabel(option)}
            </li>
          ))}
          {showNew && (
            <li
              id={optionId(matches.length)}
              data-index={matches.length}
              role="option"
              aria-selected={active === matches.length}
              className={
                active === matches.length
                  ? 'combobox-option combobox-option-new combobox-option-active'
                  : 'combobox-option combobox-option-new'
              }
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(matches.length)}
            >
              {newLabel(value.trim())}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
